import { AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { catchError, filter, finalize, flatMap, forkJoin, from, map, mergeMap, Observable, of, Subscription, switchMap, tap, throwError, toArray } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OrderSummaryComponent } from '../../component/order/order-summary/order-summary.component';
import { ENVIRONMENT_TOKEN, ErrorHandlerService, InvoiceDataProvider, StateService, UserDataProvider, WcagService } from 'libs/core/src/public-api';
import { CateringStateService } from 'libs/core/src/lib/state/catering.state.service';
import { BasketDataProvider } from 'libs/core/src/lib/data-provider/basket.data-provider';
import { HttpErrorResponse } from '@angular/common/http';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { storageKey, appProjectName } from 'libs/core/src/app.const';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { VoucherErrorEnum } from 'libs/core/src/lib/model/component/basket/voucher-error.enum';
import { VoucherModalStateEnum } from 'libs/core/src/lib/model/component/basket/voucher-modal-state.enum';
import { MessageModel, MessageType } from 'libs/core/src/lib/model/message.model';
import { BasketPageModel } from 'libs/core/src/lib/model/page/basket/basket.page.model';
import { IVoucher, OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { ScreeningItemViewModel } from 'libs/core/src/lib/model/view-model/order/screening-item/screening-item.view.model';
import { AppService } from 'libs/core/src/lib/service/app.service';
import { CountdownComponentService } from 'libs/core/src/lib/service/countdown.service';
import { MessageService } from 'libs/core/src/lib/service/message.service';
import { NavigationHelperService } from 'libs/core/src/lib/service/navigation/navigation-helper.service';
import { ResponseValidatorService } from 'libs/core/src/lib/service/validator/response-validator.service';
import { VoucherService } from 'libs/core/src/lib/service/voucher.service';
import { TicketViewModel } from 'libs/core/src/lib/model/view-model/shared/ticket/ticket.view.model';
import { ChangeTicketEvent } from '../../component/basket/basket-list/event/change-ticket.event';
import { VoucherViewModel } from 'libs/core/src/lib/model/view-model/voucher/voucher.view.model';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { ModalEventEmitter } from '../../component/modal/modal-status.enum';
import { UntilDestroy } from '@ngneat/until-destroy';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { TotalizerService } from 'libs/core/src/lib/service/totalizer.service';
import { CardDataProvider } from 'libs/core/src/lib/data-provider/card.data-provider';
import { OrderPaymentPackage } from 'libs/core/src/lib/model/order-payment.package.model';
import { CartService } from '../../component/cart/service/cart.service';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { GoogleTagManagerService } from 'libs/core/src/lib/service/analytics-services/google-tag-manager.service';
import { PaymentProviderStateService } from '../../component/payment/service/payment-provider-state.service';
import { ScreenStateService } from 'libs/core/src/lib/state/screen.state.service';
import { TitleService } from 'libs/core/src/lib/service/title.service';
import { cloneDeep } from 'lodash-es';
import { ApplePayService } from '../../component/payment/proxy/provider/apay/service/apple-pay.service';
import { OrderTicketHttpService } from 'libs/core/src/lib/http/order-ticket.http.service';
import { NavigationService } from 'libs/core/src/lib/service/navigation/navigation.service';

@UntilDestroy()
@Component({
  template: ''
})
export class BasketPageComponent implements OnInit, AfterViewInit, OnDestroy {
  loaderEnum: typeof LoaderEnum = LoaderEnum;

  @ViewChild('orderSummary') public orderSummaryComponent: OrderSummaryComponent;
  @ViewChild('stepBackConfirmTemplate') public stepBackConfirmTemplate!: TemplateRef<any>;

  public warningModalVisibled = false;
  public basketPageModel: BasketPageModel = null;
  public order: OrderViewModel = null;
  public ticketList: Array<TicketViewModel> = new Array<TicketViewModel>();
  public isOrderAgreementsRequired = false;
  public shouldStartPaymentAfterPatch = false;
  public requireExtraFees = [];
  public totalPrice = 0;
  public totalOptionalExtraFeePrice = 0;
  public enabledHeliosVoucher = false;
  public voucherError: string = null;
  public fetchedVoucher = false;
  public voucher: VoucherViewModel = null;
  public voucherModalState: VoucherModalStateEnum = null;

  public modalRef: BsModalRef;

  private deactivated = false;
  protected orderSubscription: Subscription = Subscription.EMPTY;
  protected doSubscription: Subscription = Subscription.EMPTY;

  vouchers: IVoucher[];

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    protected route: ActivatedRoute,
    protected orderService: OrderStateService,
    protected basketDataProvider: BasketDataProvider,
    protected orderDataProvider: OrderDataProvider,
    protected navigationHelperService: NavigationHelperService,
    protected messageService: MessageService,
    protected translateService: TranslateService,
    protected countdownComponentService: CountdownComponentService,
    protected voucherService: VoucherService,
    protected responseValidatorService: ResponseValidatorService,
    protected modalService: BsModalService,
    protected appService: AppService,
    protected screenStateService: ScreenStateService,
    protected cateringStateService: CateringStateService,
    protected loaderService: LoaderService,
    protected totalizerService: TotalizerService,
    protected cardDataProvider: CardDataProvider,
    protected navigationService: NavigationService,
    protected cartService: CartService,
    protected stateService: StateService,
    protected authStateService: AuthStateService,
    protected invoiceDataProvider: InvoiceDataProvider,
    protected userDataProvider: UserDataProvider,
    protected googleTagManagerService: GoogleTagManagerService,
    protected paymentProviderStateService: PaymentProviderStateService,
    protected wcagService: WcagService,
    protected errorHandlerService: ErrorHandlerService,
    protected titleService: TitleService
  ) {
    this.isOrderAgreementsRequired = this.environment.pages.basket.showAgreements;
    this.shouldStartPaymentAfterPatch = this.environment.pages.basket.autoStartPayment;
    this.enabledHeliosVoucher = this.environment.pages.basket.enabledHeliosVoucher;
  }

  public ngOnInit() {
    this.handleSubscription();
    this.titleService.setTitle(this.translateService.instant('basket.pageTitle'));
  }

  sem = false;
  handleSubscription() {
    this.orderSubscription = this.cartService.cart$
      .pipe(
        filter((cart) => !!cart),
        tap({
          next: (cart) => {
            this.basketPageModel = cloneDeep(cart);
            this.screenStateService.setData(cart.screen);
            if (this.voucherModalState === VoucherModalStateEnum.COMPLETE) {
              this.fetchedVoucher = true;
            }
          },
          error: (e) => {
            console.error(e);
            this.fetchedVoucher = false;
            this.voucherModalState = null;
          }
        }),
        switchMap(() => this.setDefaultTicketsInOrder()),
        tap({
          next: () => {
            this.requireExtraFees = this.orderSummaryComponent?.orderSummaryView?.requireExtraFees;
          }
        })
      )
      .subscribe(() => {
        this.loaderService.hideLoader(LoaderEnum.MAIN);
      });

    this.orderSubscription.add(this.orderService.state$.pipe(tap((order) => this.updateLocalOrder(order))).subscribe());
  }

  ngAfterViewInit(): void {
    this.countdownComponentService.start();
    this.countdownComponentService.visible();
  }

  public setDefaultTicketsInOrder(): Observable<OrderViewModel> {
    const order = this.orderService.getOrder();
    const everyItemsHaveTickets = order.screeningItems.every((item) => !!item.ticketId);
    this.selectDefaultTickets(order.screeningItems);

    if (order && !everyItemsHaveTickets) {
      this.orderService.setOrder(order);
    }

    if (order && !everyItemsHaveTickets) {
      if (order?.cinemaId) {
        this.displayAdditionalNotification(order.cinemaId);
      }
    }

    return of(order);
  }

  /**
   * Performed when user changes the ticket
   */
  public onChangedSeatTicket(event: ChangeTicketEvent) {
    const order = this.orderService.getOrder();
    if (!order) {
      return;
    }

    const item: ScreeningItemViewModel | undefined = order.screeningItems.find((x) => x.seatId === event.seatId);
    if (!item) {
      return;
    }

    item.ticketId = event.ticketId;
    this.updateSelectedTicketList(order.screeningItems);
  }

  public onNavigationClick(event: string) {
    switch (event) {
      case 'previous':
        this.onPreviousAction();
        break;
      case 'next':
        this.onNextAction();
        break;
    }
  }

  public onModalClick(event: string) {
    this.warningModalVisibled = false;
    this.onNextAction(event);
  }

  private deleteVouchers(): Observable<ScreeningItemViewModel[]> {
    return of(this.order?.getVouchers()).pipe(
      filter((vouchers) => !!vouchers && vouchers.length > 0),
      switchMap((vouchers) => forkJoin(vouchers.map((voucher) => from(this.deleteVoucher(voucher.number)))))
    );
  }

  public onPreviousAction(previousRoute: boolean = true) {
    const cinemaId = this.order?.cinemaId;
    const screeningId = this.order?.screeningItems[0]?.screeningId;
    if (!previousRoute) {
      this.clearSubscriber();
    }

    this.navigationService.goTo(['screen'], { queryParams: { screeningId: screeningId, cinemaId: cinemaId } });
  }

  public onNextAction(customTarget: string | null = null) {
    if (this.cateringStateService.cateringSaleEnabled) {
      if (customTarget === null) {
        this.warningModalVisibled = true;
        return;
      }
    } else {
      customTarget = 'skip';
    }

    this.orderService.setItem(storageKey.isExtraFeesSelected, 'true');
    const orderItemsWithoutSelectedTickets: Array<ScreeningItemViewModel> = this.order.screeningItems.filter(
      (element) => element.ticketId === null || element.ticketId === undefined
    );

    if (orderItemsWithoutSelectedTickets.length > 0) {
      this.messageService.add(new MessageModel(MessageType.info, this.translateService.instant('errors.102')));
      return;
    }

    this.loaderService.showLoader(LoaderEnum.MAIN);

    const observableChain: Array<Observable<any>> = new Array<Observable<any>>();

    if (this.isOrderAgreementsRequired === true) {
      const agreements = this.basketPageModel.agreements?.filter((agreement) => agreement.checked === true).map((element) => element.id);

      observableChain.push(this.orderDataProvider.updateAgreements(this.order.cinemaId, this.order.id, agreements));
    } else {
      observableChain.push(of(null));
    }

    observableChain.push(this.orderDataProvider.patchItems(this.order.cinemaId, this.order, this.order.screeningItems));

    forkJoin(observableChain).subscribe(([s, order]: [any, OrderViewModel]) => {
      this.orderService.setOrder(order);

      if (this.shouldStartPaymentAfterPatch === true) {
        const orderPaymentPackage: OrderPaymentPackage = new OrderPaymentPackage();
        orderPaymentPackage.cinemaId = this.order.cinemaId;
        orderPaymentPackage.orderId = this.order.id;
        orderPaymentPackage.channel = this.environment.constants.paymentChannel;
        orderPaymentPackage.paymentProviderIdentifier = this.environment.constants.autoSelectPaymentMethod;

        this.orderDataProvider.postPayment(orderPaymentPackage).subscribe({
          next: (paymentResponse) => {
            this.loaderService.hideLoader(LoaderEnum.MAIN);
            this.orderService.removeOrder();
            this.orderSubscription.unsubscribe();
            window.location.href = paymentResponse.plainPayload;
          },
          error: (e) => {
            console.log(e);
            this.loaderService.hideLoader(LoaderEnum.MAIN);
          }
        });
      } else {
        if (customTarget === null) {
          this.navigationService.goTo([this.navigationHelperService.getNextRoute(this.route.snapshot)]);
        } else {
          this.navigationService.goTo([this.navigationHelperService.getRouteFor(customTarget, this.route.snapshot)]);
        }
        return;
      }
    });
  }

  public ngOnDestroy(): void {
    this.clearSubscriber();
    this.fetchedVoucher = false;
    this.voucherModalState = null;
  }

  public clearSubscriber() {
    if (this.orderSubscription !== Subscription.EMPTY) {
      this.orderSubscription.unsubscribe();
      this.orderSubscription = Subscription.EMPTY;
    }
  }

  public onVoucherAddModal(eventEmitter: ModalEventEmitter) {
    this.loaderService.showLoader(LoaderEnum.MAIN);
    this.voucherError = null;
    let isFetched = false;

    this.orderDataProvider
      .patchItems(this.order.cinemaId, this.order, this.order.screeningItems)
      .pipe(
        mergeMap((result) => {
          const limit = eventEmitter.count;
          const orderItems = result.screeningItems;

          if (limit > 0) {
            return this.voucherService
              .assignVoucherToOrder(this.order.cinemaId, this.order.id, this.voucher.voucherNumber, this.voucherUses === limit ? null : limit)
              .pipe(
                tap((order) => this.orderService.setOrder(order)),
                map((order) => order.voucher)
              );
          }

          if (limit === 0) {
            isFetched = true;
            return of(null);
          }

          const orderItemsArray: ScreeningItemViewModel[] = [];
          for (let i = 0; i < orderItems.length; i++) {
            if (this.voucher.positions.find((ps) => ps.ticketId === orderItems[i].ticketId) && orderItemsArray.length < limit) {
              orderItemsArray.push(orderItems[i]);
            }
          }

          if (orderItemsArray.length < 1) {
            isFetched = true;

            this.canNotUseVoucherError();
            return of(null);
          }

          return from(orderItemsArray).pipe(
            flatMap((item) =>
              this.voucherService.assignVoucherToOrderItem(this.order.cinemaId, this.order.id, item.id, this.voucher.voucherNumber, this.voucher.voucherName)
            ),
            toArray()
          );
        })
      )
      .subscribe({
        next: (x) => {
          const order = this.orderService.getOrder();
          this.vouchers = order?.getVouchers();

          if (this.vouchers?.length <= 0 || !this.vouchers.some((v) => v.number === this.voucher.voucherNumber)) {
            this.canNotUseVoucherError();
          }

          this.fetchedVoucher = x !== null;

          if (isFetched) {
            this.loaderService.hideLoader(LoaderEnum.MAIN);
          } else {
            this.voucherModalState = VoucherModalStateEnum.COMPLETE;
            this.updateLocalOrder(order);
          }
        },
        error: (e) => {
          this.fetchedVoucher = false;
          if (e && e.error && e.error.error && e.error.error.code === 0) {
            this.canNotUseVoucherError();
          }

          this.handleError(e);

          if (isFetched) {
            this.loaderService.hideLoader(LoaderEnum.MAIN);
          }
        }
      });
  }

  private canNotUseVoucherError() {
    this.voucherError = this.translateService.instant(`order.orderSummary.voucher.input.error.${VoucherErrorEnum.CANNOT_USE}`);
  }

  public onVoucherModalClose(eventEmitter: ModalEventEmitter) {
    this.fetchedVoucher = false;
    this.voucherModalState = null;
  }

  public get voucherUses() {
    if (this.voucher.usesLeft === -1) {
      return this.order.screeningItems.length;
    }

    return this.voucher.usesLeft;
  }

  public onVoucherItemCheck(voucherNumber) {
    this.orderService.setItem(storageKey.isExtraFeesSelected, 'true');
    this.voucherError = null;
    this.loaderService.showLoader(LoaderEnum.MAIN);
    this.orderDataProvider
      .patchItems(this.order.cinemaId, this.order, this.order.screeningItems)
      .pipe(
        switchMap((order) => this.voucherService.assignVoucherToOrder(order.cinemaId, order.id, voucherNumber)),
        tap({
          next: (order) => {
            this.vouchers = this.orderService.getVouchers();
            this.updateLocalOrder(order);
          },
          error: (e) => {
            this.handleError(e);
            this.setVoucherError(e);
          }
        })
      )
      .subscribe();
  }

  protected setVoucherError(e: any) {
    this.voucherError = this.errorHandlerService.getMessage(e, null, true);
  }

  public displayAdditionalNotification(cinemaId: string) {}

  public deleteVoucher(voucherNumber: string): Observable<ScreeningItemViewModel> {
    this.voucherError = null;
    this.loaderService.showLoader(LoaderEnum.MAIN);

    return this.voucherService.removeVoucherFromOrder(this.order.cinemaId, this.order.id, voucherNumber).pipe(
      tap(() => {
        const order = this.orderService.getOrder();
        this.vouchers = this.orderService.getVouchers();
        this.updateLocalOrder(order);
      }),
      catchError((e) => {
        this.handleError(e);
        return throwError(e);
      }),
      finalize(() => {
        this.loaderService.hideLoader(LoaderEnum.MAIN);
      })
    );
  }

  public onCardDelete(cardNumber: string) {
    this.loaderService.showLoader(LoaderEnum.MAIN);
    this.cardDataProvider.deleteCardFromOrder(this.order.cinemaId, this.order.id).subscribe({
      next: () => {
        this.loaderService.hideLoader(LoaderEnum.MAIN);
        window.location.reload();
      },
      error: (e) => this.handleError(e)
    });
  }

  public onSingleVoucherItemDelete(item: ScreeningItemViewModel) {
    this.loaderService.showLoader(LoaderEnum.MAIN);

    this.voucherService.removeVoucherFromOrderItem(this.order.cinemaId, this.order.id, item.id, item.voucherNumber).subscribe({
      next: () => {
        this.vouchers = this.orderService.getVouchers();
        const order = this.orderService.getOrder(); //TODO potencjalny problem z brakiem informacji o order
        this.updateLocalOrder(order);
      },
      error: (e) => this.handleError(e)
    });
  }

  /**
   * Selects default tickets
   */
  protected selectDefaultTickets(screeningItems: ScreeningItemViewModel[]): ScreeningItemViewModel[] {
    if (!screeningItems?.length) {
      return [];
    }

    for (const screeningItem of screeningItems) {
      if (screeningItem.ticketId) {
        continue;
      }

      const availableTickets = this.basketPageModel.findTicketsByScreeningItemId(screeningItem.id);
      if (!availableTickets?.length) {
        continue;
      }

      const defaultTicket = this.cartService.defaultTicketStrategyContext.getDefaultTicket(availableTickets);
      if (defaultTicket?.id) {
        screeningItem.ticketId = defaultTicket.id;
      }
    }

    this.updateSelectedTicketList(screeningItems);
    return screeningItems;
  }

  /**
   * Updates selected ticket list
   */
  protected updateSelectedTicketList(screeningItems: ScreeningItemViewModel[]): void {
    const selectedTicketList: TicketViewModel[] = [];
    screeningItems.forEach((orderItem) => {
      const ticket: TicketViewModel | undefined = this.basketPageModel.findTicketBySeatIdAndTicketId(orderItem.seatId, orderItem.ticketId);
      if (ticket) {
        selectedTicketList.push(ticket);
      }
    });

    this.ticketList = selectedTicketList;
    this.orderService.changeSummaryStateEmit();
  }

  protected updateLocalOrder(order) {
    this.order = order;
  }

  protected handleError(err: HttpErrorResponse) {
    const errStatus = err && err.status ? err.status : null;
    this.loaderService.hideLoader(LoaderEnum.MAIN);

    if (errStatus === 400) {
      const isHandled: boolean = this.handleValidationError(err);
      if (isHandled === false) {
        return this.responseValidatorService.viewErrors(err);
      }
    }
  }

  /**
   * Handles an well described errors
   */
  private handleValidationError(err: HttpErrorResponse): boolean {
    let infoCode = err.error && err.error.error && err.error.error.info_code ? err.error && err.error.error && err.error.error.info_code : null;

    if (infoCode && typeof infoCode !== 'string') {
      infoCode = (infoCode as any).toString();
    }

    let translationResult = false;

    if (infoCode !== null) {
      const translationKey = `errors.${infoCode}`;
      const errorContentTranslation: string = this.translateService.instant(translationKey);

      if (errorContentTranslation !== translationKey) {
        this.messageService.clear();
        this.messageService.add(new MessageModel(MessageType.danger, errorContentTranslation, infoCode));
        translationResult = true;
      }
    }
    return translationResult;
  }

  public showStepBackModal(template: TemplateRef<any>) {
    this.loaderService.hideLoader(LoaderEnum.MAIN);
    this.modalRef = this.modalService.show(template, { class: 'modal-sm' });
  }

  public onStepBackModalClick(deactivate: boolean) {
    this.modalRef.hide();
    if (!deactivate) {
      return;
    }

    if (this.appService.isProject(appProjectName.HELIOS)) {
      this.countdownComponentService.destroy();
    }

    this.deactivated = true;
    this.deleteVouchers().subscribe({
      complete: () => {
        this.clearSubscriber();
        this.onPreviousAction(false);
      }
    });
  }

  public canDeactivate(): Observable<boolean> {
    if (this.deactivated || !this.stepBackConfirmTemplate) {
      return of(true);
    }

    this.showStepBackModal(this.stepBackConfirmTemplate);
    return of(false);
  }

  get seatIds(): string[] {
    return this.order.screeningItems.map((item) => item.seatId);
  }
}
